import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function InvoiceSign() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState("draw"); // draw | upload
  const [signerName, setSignerName] = useState("");
  const [uploadedSig, setUploadedSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);

  useEffect(() => {
    api.get(`/invoice/sign/${token}`)
      .then((r) => { setMeta(r.data); setSignerName(r.data.customer_name || ""); })
      .catch((e) => setErr(e?.response?.data?.detail || "This link is invalid."));
  }, [token]);

  const getCtx = () => canvasRef.current?.getContext("2d");

  useEffect(() => {
    if (mode !== "draw" || !canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [mode]);

  const ptFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX || 0) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY || 0) - rect.top;
    return { x: (x / rect.width) * canvasRef.current.width, y: (y / rect.height) * canvasRef.current.height };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; lastPt.current = ptFromEvent(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = ptFromEvent(e);
    const ctx = getCtx();
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPt.current = p;
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 300_000) { toast.error("Max 300 KB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setUploadedSig(ev.target.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    let dataUrl = null;
    if (mode === "draw") {
      // Empty check: scan for any non-transparent pixel
      const c = canvasRef.current;
      const ctx = c.getContext("2d");
      const px = ctx.getImageData(0, 0, c.width, c.height).data;
      let inked = false;
      for (let i = 3; i < px.length; i += 4) { if (px[i] > 0) { inked = true; break; } }
      if (!inked) { toast.error("Please draw your signature"); return; }
      dataUrl = c.toDataURL("image/png");
    } else {
      if (!uploadedSig) { toast.error("Upload a signature image"); return; }
      dataUrl = uploadedSig;
    }
    setBusy(true);
    try {
      await api.post(`/invoice/sign/${token}`, { signature_data_url: dataUrl, signer_name: signerName.trim() || null });
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (err) {
    return <Shell><Lucide.AlertOctagon size={40} className="text-amber-300 mx-auto" /><h1 className="bx-heading text-2xl font-extrabold mt-4">Cannot sign this invoice</h1><p className="text-slate-400 text-sm mt-2">{err}</p></Shell>;
  }
  if (!meta) return <Shell><Lucide.Loader2 className="animate-spin mx-auto text-purple-300" /></Shell>;

  if (done || meta.already_signed) {
    return (
      <Shell>
        <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring" }} className="mx-auto w-20 h-20 rounded-full flex items-center justify-center" style={{ background: "radial-gradient(circle, rgba(16,185,129,0.25) 0%, transparent 70%)" }}>
          <Lucide.CheckCircle2 size={56} className="text-emerald-300" strokeWidth={1.8} />
        </motion.div>
        <h1 className="bx-heading text-2xl font-extrabold mt-5">Signed — thank you!</h1>
        <p className="text-slate-400 text-sm mt-2">{meta.company_name} has been notified. You can close this page.</p>
      </Shell>
    );
  }

  const symb = meta.currency === "INR" ? "₹" : "$";

  return (
    <Shell>
      <Seo title={`Sign invoice ${meta.invoice_no}`} description="Digitally sign your invoice." />
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{meta.company_name} · Signature requested</div>
      <h1 className="bx-heading text-2xl font-extrabold mt-1">Invoice {meta.invoice_no}</h1>
      <p className="text-slate-400 text-sm mt-2">{meta.customer_name && <>Hi {meta.customer_name.split(" ")[0]}, </>}please sign below to acknowledge invoice of <strong className="text-purple-300">{symb}{Number(meta.grand_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>.</p>

      <div className="flex gap-2 mt-5 justify-center">
        <button data-testid="mode-draw" onClick={() => setMode("draw")} className={`bx-chip ${mode === "draw" ? "ring-1 ring-purple-400" : ""}`}><Lucide.PenTool size={12} className="inline" /> Draw</button>
        <button data-testid="mode-upload" onClick={() => setMode("upload")} className={`bx-chip ${mode === "upload" ? "ring-1 ring-purple-400" : ""}`}><Lucide.Upload size={12} className="inline" /> Upload</button>
      </div>

      {mode === "draw" ? (
        <div className="mt-4">
          <div className="rounded-xl bg-white relative" style={{ touchAction: "none" }}>
            <canvas
              ref={canvasRef}
              width={600}
              height={220}
              data-testid="sig-canvas"
              className="w-full h-44 rounded-xl cursor-crosshair"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            />
            <div className="absolute bottom-2 left-3 text-[10px] text-slate-400 pointer-events-none">Sign here</div>
          </div>
          <button onClick={clear} className="text-xs text-slate-400 hover:text-slate-200 mt-2"><Lucide.Eraser size={11} className="inline" /> Clear</button>
        </div>
      ) : (
        <div className="mt-4">
          {uploadedSig ? (
            <div className="rounded-xl bg-white p-4">
              <img src={uploadedSig} alt="signature" className="max-h-32 mx-auto" />
            </div>
          ) : (
            <label className="block rounded-xl border-2 border-dashed border-white/15 p-10 text-center cursor-pointer hover:border-purple-400/40 transition">
              <Lucide.Upload size={28} className="mx-auto text-slate-500 mb-2" />
              <div className="text-sm text-slate-300">Click to upload PNG / JPG</div>
              <div className="text-[10px] text-slate-500 mt-1">Max 300 KB</div>
              <input type="file" accept="image/png,image/jpeg" hidden onChange={onUpload} data-testid="sig-upload-input" />
            </label>
          )}
          {uploadedSig && <button onClick={() => setUploadedSig(null)} className="text-xs text-slate-400 hover:text-slate-200 mt-2">Choose different file</button>}
        </div>
      )}

      <input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Your name (optional)" className="bx-input mt-4" data-testid="signer-name" />

      <button data-testid="sig-submit" onClick={submit} disabled={busy} className="w-full mt-4 rounded-xl py-3 font-bold text-base disabled:opacity-50 flex items-center justify-center gap-2" style={{ background: "linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)", boxShadow: "0 12px 30px -10px rgba(139,92,246,0.6)" }}>
        {busy ? <Lucide.Loader2 className="animate-spin" size={16} /> : <Lucide.CheckCircle2 size={16} />} Submit signature
      </button>
      <p className="text-[10px] text-slate-500 text-center mt-3">By signing, you confirm receipt of the invoice. Your signature will be embedded on the PDF.</p>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#05050A] text-slate-100 px-5 py-10 flex items-center justify-center">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bx-card p-7 max-w-md w-full" data-testid="invoice-sign-page" style={{ background: "linear-gradient(180deg, rgba(124,58,237,0.10) 0%, rgba(5,5,10,1) 100%)" }}>
        {children}
      </motion.div>
    </div>
  );
}
